// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { bool, object } from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  SkeletonText,
} from '@carbon/react';
import {
  Megaphone,
  PiggyBank,
  PanelTopDashed,
  Package,
  ClockAlert,
  ReceiptText,
} from 'lucide-react';

import { AnalyticsLink, Can } from 'Components/shared';
import { PERMISSION } from 'Constants';

import weeklyPromotionsBG from 'Assets/images/background1.png';
import stockClearingsBG from 'Assets/images/background2.png';
import resealedProductsBG from 'Assets/images/background3.png';
import ordersProcessingBG from 'Assets/images/background4.png';
import overduePaymentsBG from 'Assets/images/background5.png';
import invoicesIssuedBG from 'Assets/images/background6.png';

const StyledBadgesSection = styled.section`
  margin: 20px 0 40px;

  ul {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;

    li {
      display: flex;
      align-items: start;
      justify-content: start;
      flex: 1;
      height: 120px;
      border-radius: 10px;
      box-shadow: 2px 2px 4px #d6d6d6;
      background-color: #fff;
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
      
      &.weeklyPromotions {
        background-image: url(${weeklyPromotionsBG});
      }

      &.stockClearings {
        background-image: url(${stockClearingsBG});
      }

      &.resealedProducts {
        background-image: url(${resealedProductsBG});
      }

      &.ordersProcessing {
        background-image: url(${ordersProcessingBG});
      }

      &.overduePayments {
        background-image: url(${overduePaymentsBG});
      }

      &.invoicesIssued {
        background-image: url(${invoicesIssuedBG});
      }

      a {
        display: flex;
        gap: 15px;
        align-items: start;
        justify-content: start;
        width: 100%;
        height: 100%;
        padding: 20px;

        .icon {
          color: #FF7900;
        }

        .info {
          display: flex;
          flex-direction: column;

          .value {
            font-size: 24px;
            line-height: 32px;
            font-weight: 600;
          }
        }
      }
    }
  }

  @media screen and (max-width: 960px) {
    .badges {
      margin: 25px 0;

      ul li {
        min-width: 100%;
        display: flex;
        justify-content: space-between;

        a {
          justify-content: flex-start;
          min-width: 100%;

          .icon {
            min-width: 102px;
            padding-right: 32px;
          }

          .info {
            align-items: flex-start;
          }

          .mobileCaret {
            color: #999;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 30px;
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
  
            svg {
              transform: rotate(270deg);
            }
          }
        }

      }
    }

    .productsLink {
      display: flex;
    }

    .banners {
      flex-direction: column;
    }

    .notifications .notificationsWrapper {
      flex-direction: column;

      .notification {
        flex: 1;
        max-width: 100%;
      }
    }
  }

  @media screen and (max-width: 767px) {
    .badges ul li a .info span {
      text-align: left;
    }
  }
`;

const BadgesSection = ({
  badges,
  badgesLoading,
}) => {
  const { t } = useTranslation();

  return (
    <StyledBadgesSection>
      <ul>
        <li className="weeklyPromotions">
          <AnalyticsLink to="/promotions" event={ { action: 'promotions_badge_clicked' } }>
            <div className="icon">
              <Megaphone size={ 32 } />
            </div>
            <div className="info">
              { !badges && badgesLoading ? <SkeletonText className="badgeSkeleton" /> : <span className="value">{ badges?.activePromotions }</span> }
              <span className="label">{ t('pages.badges.promotions') }</span>
            </div>
          </AnalyticsLink>
        </li>
        <li className="stockClearings">
          <AnalyticsLink to="/products?stock_clearing_sale=1" event={ { action: 'stock_clearing_badge_clicked' } }>
            <div className="icon">
              <PiggyBank size={ 32 } />
            </div>
            <div className="info">
              { !badges && badgesLoading ? <SkeletonText className="badgeSkeleton" /> : <span className="value">{ badges?.stockClearings }</span> }
              <span className="label">{ t('pages.badges.stockLiquidations') }</span>
            </div>
          </AnalyticsLink>
        </li>
        <li className="resealedProducts">
          <AnalyticsLink to="/products?only_resealed=1" event={ { action: 'resealed_products_badge_clicked' } }>
            <div className="icon">
              <PanelTopDashed size={ 32 } />
            </div>
            <div className="info">
              { !badges && badgesLoading ? <SkeletonText className="badgeSkeleton" /> : <span className="value">{ badges?.resealedProducts }</span> }
              <span className="label">{ t('pages.badges.resealedProducts') }</span>
            </div>
          </AnalyticsLink>
        </li>
        <Can permission={ PERMISSION.VIEW_ACCOUNT_STATUS }>
          <li className="ordersProcessing">
            <AnalyticsLink to="/orders?internal_status_id=1" event={ { action: 'processing_orders_badge_clicked' } }>
              <div className="icon">
                <Package size={ 32 } />
              </div>
              <div className="info">
                { !badges && badgesLoading ? <SkeletonText className="badgeSkeleton" /> : <span className="value">{ badges?.openedOrders }</span> }
                <span className="label">{ t('pages.badges.processingOrders') }</span>
              </div>
            </AnalyticsLink>
          </li>
          <li className="overduePayments">
            <AnalyticsLink to="/reports/invoices?status=4" event={ { action: 'outstanding_payments_badge_clicked' } }>
              <div className="icon">
                <ClockAlert size={ 32 } />
              </div>
              <div className="info">
                { !badges && badgesLoading ? <SkeletonText className="badgeSkeleton" /> : <span className="value">{ badges?.overduePayments }</span> }
                <span className="label">{ t('pages.badges.outstandingPayments') }</span>
              </div>
            </AnalyticsLink>
          </li>
          <li className="invoicesIssued">
            <AnalyticsLink to="/reports/invoices" event={ { action: 'invoices_issued_badge_clicked' } }>
              <div className="icon">
                <ReceiptText size={ 32 } />
              </div>
              <div className="info">
                { !badges && badgesLoading ? <SkeletonText className="badgeSkeleton" /> : <span className="value">{ badges?.issuedInvoices }</span> }
                <span className="label">{ t('pages.badges.invoicesIssued') }</span>
              </div>
            </AnalyticsLink>
          </li>
        </Can>
      </ul>
    </StyledBadgesSection>
  );
};

BadgesSection.propTypes = {
  badges: object,
  badgesLoading: bool.isRequired,
};

BadgesSection.displayName = 'BadgesSection';

export default BadgesSection;
